import { FC, KeyboardEvent, useRef, useState } from 'react';
import cx from 'classnames';
import Field from '../../../components/shared/Field/Field';
import TooltipContainer from '../../../components/shared/TooltipContainer/TooltipContainer';
import { CellValue, parseCellInput } from './cellInput';
import styles from './customRowValueCell.module.scss';

type Props = {
  value: string;
  isText: boolean;
  unit?: string;
  readOnly: boolean;
  onSave: (value: CellValue) => void;
};

export const CustomRowValueCell: FC<Props> = ({ value, isText, unit, readOnly, onSave }) => {
  const [draft, setDraft] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const shownUnit = isText ? undefined : unit;

  if (readOnly) return <span>{value}{value && shownUnit && <>&nbsp;{shownUnit}</>}</span>;

  const save = () => {
    const parsed = parseCellInput(draft, isText);
    setDraft(parsed.display);
    if (parsed.display !== value) onSave(parsed.value);
  };

  const blurOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const checkTruncation = () => {
    const input = inputRef.current;
    setIsTruncated(!!input && input.scrollWidth > input.clientWidth);
  };

  const field = (
    <Field
      ref={inputRef}
      className={cx(styles.cell, isText && styles.text)}
      style={{ textAlign: isText ? 'left' : 'right' }}
      value={draft}
      onChange={setDraft}
      onFocus={() => setIsEditing(true)}
      onBlur={() => {
        setIsEditing(false);
        save();
      }}
      onKeyDown={blurOnEnter}
      unit={shownUnit}
    />
  );

  if (!isText) return field;

  return (
    <TooltipContainer
      renderReferenceComponent={(className, ref) => (
        <div className={className} ref={ref} onMouseEnter={checkTruncation}>
          {field}
        </div>
      )}
    >
      {isTruncated && !isEditing && <div className={styles.tooltip}>{draft}</div>}
    </TooltipContainer>
  );
};
